import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { useNavigate } from "react-router-dom";
import { collection, getDocs, query, where, doc, getDoc } from "firebase/firestore";
import {
  Card, CircularProgress, Avatar, Box, Typography, Divider,
  Stack, Grid, Button, Chip, Container, List, ListItem, ListItemText
} from "@mui/material";
import {
  People, AccountBalanceWallet, Assessment, Payment,
  PriceCheck, HourglassEmpty, EventAvailable, TrendingUp
} from "@mui/icons-material";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";

const PRIMARY = "#0d9488";
const SECONDARY = "#64748b";
const BG = "#f1f5f9";
const DARK = "#0f172a";
const COLORS = ["#0d9488", "#f59e0b", "#ef4444", "#3b82f6"];

function Dashboard() {
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState("");

  // Payroll Metrics State
  const [payrollSummary, setPayrollSummary] = useState({
    totalEmployees: 0,
    allocatedSalaryBudget: 0,
    pendingAdvances: 0,
    totalAdvancesAmount: 0,
    processedPaymentsThisMonth: 0,
    totalPaidAmountThisMonth: 0,
  });

  const [recentAdvances, setRecentAdvances] = useState([]);
  const [attendanceOverview, setAttendanceOverview] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);

  // Authenticate User
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDoc = await getDoc(doc(db, "authroles", user.uid));
        setUserData(userDoc.exists() ? userDoc.data() : user);
      } else {
        navigate("/login");
      }
    });
    return () => unsub();
  }, [navigate]);

  // Set current month string format (YYYY-MM)
  useEffect(() => {
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    setSelectedMonth(`${today.getFullYear()}-${month}`);
  }, []);

  // Fetch Payroll Data Matrix in Parallel
  useEffect(() => {
    const fetchPayrollData = async () => {
      setLoading(true);
      try {

        // Parallel execution pipeline
        const [staffSnap, allocationsSnap, advancesSnap, paymentsSnap, attendanceSnap] = await Promise.all([
          getDocs(query(collection(db, "authroles"), where("role", "in", ["Admin", "Staff"]))),
          getDocs(query(collection(db, "salaryAllocations"))),
          getDocs(query(collection(db, "staffadvances"))),
          getDocs(query(collection(db, "monthlyPayments"), where("month", "==", selectedMonth))),
          getDocs(query(collection(db, "staffattendance")))
        ]);

        // 1. Process Employees
        const totalEmployees = staffSnap.size;

        // Compute Department counts for distribution chart
        const deptMap = {};
        staffSnap.forEach(d => {
          const role = d.data().role || "Staff";
          deptMap[role] = (deptMap[role] || 0) + 1;
        });
        const processedDepts = Object.keys(deptMap).map(key => ({ name: key, value: deptMap[key] }));
        setDepartmentData(processedDepts);

        // 2. Process Salary Budget Allocations
        let totalBudget = 0;
        allocationsSnap.forEach(d => {
          totalBudget += Number(d.data().baseSalary || 0);
        });

        // 3. Process Staff Advances/Loans
        let pendingAdvancesCount = 0;
        let totalAdvancesAmt = 0;
        const advancesList = [];

        advancesSnap.forEach(d => {
          const data = d.data();
          if (data.status === "Pending") {
            pendingAdvancesCount++;
            totalAdvancesAmt += Number(data.amount || 0);
            advancesList.push({ id: d.id, ...data });
          }
        });
        setRecentAdvances(advancesList.slice(0, 5)); // Keep latest 5 pending tracking streams

        // 4. Process Current Month's Payments
        let paidCount = 0;
        let totalPaidAmt = 0;
        paymentsSnap.forEach(d => {
          paidCount++;
          totalPaidAmt += Number(d.data().netPaid || 0);
        });

        // 5. Process Attendance Summary Metrics for Current Month Matrix
        let present = 0, absent = 0, leave = 0;
        attendanceSnap.forEach(d => {
          const data = d.data();
          if (data.date && data.date.startsWith(selectedMonth)) {
            if (data.status === "Present") present++;
            else if (data.status === "Absent") absent++;
            else if (data.status === "Leave") leave++;
          }
        });

        setAttendanceOverview([
          { name: "Present Days", value: present },
          { name: "Absent Days", value: absent },
          { name: "Approved Leaves", value: leave }
        ]);

        // Map calculation parameters safely to main State management pipeline
        setPayrollSummary({
          totalEmployees,
          allocatedSalaryBudget: totalBudget,
          pendingAdvances: pendingAdvancesCount,
          totalAdvancesAmount: totalAdvancesAmt,
          processedPaymentsThisMonth: paidCount,
          totalPaidAmountThisMonth: totalPaidAmt,
        });

      } catch (err) {
        console.error("Payroll Matrix Processing Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPayrollData();
  }, [userData, selectedMonth]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh', justifyContent: 'center', alignItems: 'center', background: BG }}>
        <CircularProgress style={{ color: PRIMARY }} />
      </Box>
    );
  }

  // Financial budget analytics distribution visualization configurations
  const financeChartData = [
    { name: "Disbursed Salary", amount: payrollSummary.totalPaidAmountThisMonth },
    { name: "Remaining Budget", amount: Math.max(0, payrollSummary.allocatedSalaryBudget - payrollSummary.totalPaidAmountThisMonth) },
    { name: "Pending Advances", amount: payrollSummary.totalAdvancesAmount }
  ];

  return (
    <Box sx={{ background: BG, minHeight: "100vh", p: 2 }}>
      <Container maxWidth="xl" disableGutters>

        {/* Top Header Information Stream */}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={4} spacing={2}>
          <Box>
            <Typography variant="h5" fontWeight={900} color={DARK} sx={{ letterSpacing: '-0.5px' }}>
              Payroll Management Dashboard
            </Typography>
            <Typography variant="body2" color={SECONDARY} fontWeight={500}>
              Workforce Operations • Target Month: <span style={{ color: PRIMARY, fontWeight: 700 }}>{selectedMonth}</span>
            </Typography>
          </Box>
        </Stack>

        {/* Metrics Grid Layer */}
        <Grid className="row" spacing={3} mb={4}>
          <Grid className="col-sm-3 mb-2" item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" fontWeight={700} color={SECONDARY} sx={{ textTransform: 'uppercase' }}>Total Workforce</Typography>
                  <Typography variant="h4" fontWeight={800} color={DARK} sx={{ mt: 0.5 }}>{payrollSummary.totalEmployees}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: `${PRIMARY}15`, color: PRIMARY, width: 48, height: 48, borderRadius: 3 }}><People /></Avatar>
              </Stack>
            </Card>
          </Grid>

          <Grid className="col-sm-3 mb-2" item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" fontWeight={700} color={SECONDARY} sx={{ textTransform: 'uppercase' }}>Monthly Salary Budget</Typography>
                  <Typography variant="h4" fontWeight={800} color={DARK} sx={{ mt: 0.5 }}>₹{payrollSummary.allocatedSalaryBudget.toLocaleString()}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#3b82f615', color: '#3b82f6', width: 48, height: 48, borderRadius: 3 }}><AccountBalanceWallet /></Avatar>
              </Stack>
            </Card>
          </Grid>

          <Grid className="col-sm-3 mb-2" item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" fontWeight={700} color={SECONDARY} sx={{ textTransform: 'uppercase' }}>Disbursed This Month</Typography>
                  <Typography variant="h4" fontWeight={800} color="#0f766e" sx={{ mt: 0.5 }}>₹{payrollSummary.totalPaidAmountThisMonth.toLocaleString()}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#14b8a615', color: '#14b8a6', width: 48, height: 48, borderRadius: 3 }}><TrendingUp /></Avatar>
              </Stack>
            </Card>
          </Grid>

          <Grid className="col-sm-3 mb-2" item xs={12} sm={6} md={3}>
            <Card sx={{ p: 2.5, borderRadius: 4, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="caption" fontWeight={700} color={SECONDARY} sx={{ textTransform: 'uppercase' }}>Pending Staff Advances</Typography>
                  <Typography variant="h4" fontWeight={800} color="#b45309" sx={{ mt: 0.5 }}>₹{payrollSummary.totalAdvancesAmount.toLocaleString()}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#f59e0b15', color: '#f59e0b', width: 48, height: 48, borderRadius: 3 }}><HourglassEmpty /></Avatar>
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Operational Analytics Segment Maps */}
        <Grid className="row" spacing={3}>

          {/* Chart Core Element 1: Budget Allocation Progress Metrics */}
          <Grid className="col-sm-6 mb-2" item xs={12} md={6}>
            <Card sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', minHeight: 380 }}>
              <Typography variant="subtitle1" fontWeight={800} color={DARK} mb={2}>Financial Disbursal Breakdown</Typography>
              <Box sx={{ width: "100%", height: 260 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={financeChartData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" stroke={SECONDARY} fontSize={12} />
                    <YAxis stroke={SECONDARY} fontSize={12} />
                    <Tooltip cursor={{ fill: 'rgba(0,0,0,0.02)' }} />
                    <Bar dataKey="amount" fill={PRIMARY} radius={[4, 4, 0, 0]} barSize={45}>
                      {financeChartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Card>
          </Grid>

          {/* Chart Core Element 2: Monthly Time Tracking Attendance Overview */}
          <Grid  className="col-sm-3 mb-2" item xs={12} md={3}>
            <Card sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', minHeight: 380, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle1" fontWeight={800} color={DARK} mb={1}>Time & Attendance Metrics</Typography>
              <Typography variant="caption" color={SECONDARY} mb={2}>Active month logs distribution</Typography>
              <Box sx={{ flexGrow: 1, height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={attendanceOverview} innerRadius={60} outerRadius={80} paddingAngle={4} dataKey="value" stroke="none">
                      {attendanceOverview.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip cornerRadius={8} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={1} mt={1}>
                {attendanceOverview.map((item, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[i] }} />
                      <Typography variant="caption" color={SECONDARY} fontWeight={600}>{item.name}</Typography>
                    </Stack>
                    <Typography variant="caption" fontWeight={700} color={DARK}>{item.value} Units</Typography>
                  </Stack>
                ))}
              </Stack>
            </Card>
          </Grid>

          {/* Chart Core Element 3: Department / Role Demographics Matrix */}
          <Grid  className="col-sm-3 mb-2" item xs={12} md={3}>
            <Card sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0', minHeight: 380, display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle1" fontWeight={800} color={DARK} mb={1}>Headcount Allocation</Typography>
              <Typography variant="caption" color={SECONDARY} mb={2}>Distribution across system definitions</Typography>
              <Box sx={{ flexGrow: 1, height: 180 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={departmentData} innerRadius={0} outerRadius={75} paddingAngle={2} dataKey="value" stroke="none">
                      {departmentData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[(index + 2) % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip cornerRadius={8} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={1} mt={1}>
                {departmentData.map((item, i) => (
                  <Stack key={i} direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLORS[(i + 2) % COLORS.length] }} />
                      <Typography variant="caption" color={SECONDARY} fontWeight={600}>{item.name}</Typography>
                    </Stack>
                    <Typography variant="caption" fontWeight={700} color={DARK}>{item.value} Staff</Typography>
                  </Stack>
                ))}
              </Stack>
            </Card>
          </Grid>
        </Grid>

        {/* Operational Streams: Pending Advances Workflow */}
        <Box mt={3}>
          <Grid className="row" spacing={3}>
            <Grid className="col-sm-12" item xs={12}>
              <Card sx={{ p: 3, borderRadius: 4, border: '1px solid #e2e8f0' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={800} color={DARK}>Pending Advances & Advance Adjustments Queue</Typography>
                    <Typography variant="caption" color={SECONDARY}>Action required before processing next processing generation slips</Typography>
                  </Box>
                  <Chip label={`${payrollSummary.pendingAdvances} Requests Pending`} size="small" sx={{ bgcolor: '#fff7ed', color: '#c2410c', fontWeight: 700 }} />
                </Stack>
                <Divider sx={{ mb: 2 }} />

                {recentAdvances.length === 0 ? (
                  <Typography variant="body2" color={SECONDARY} textAlign="center" py={4}>
                    No pending employee advances found for this payment matrix.
                  </Typography>
                ) : (
                  <List disablePadding>
                    {recentAdvances.map((advance, idx) => (
                      <ListItem key={advance.id} sx={{ px: 0, py: 1.5, borderBottom: idx !== recentAdvances.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
                          <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar sx={{ bgcolor: PRIMARY, color: '#fff', fontWeight: 'bold' }}>{advance.staffName?.[0] || 'S'}</Avatar>
                            <Box>
                              <Typography variant="body2" fontWeight={700} color={DARK}>{advance.staffName || "Unknown Employee"}</Typography>
                              <Typography variant="caption" color={SECONDARY} display="block">Reason: {advance.reason || "Not Provided"}</Typography>
                            </Box>
                          </Stack>
                          <Stack alignItems="flex-end">
                            <Typography variant="body2" fontWeight={800} color="#b45309">₹{Number(advance.amount || 0).toLocaleString()}</Typography>
                            <Typography variant="caption" color={SECONDARY}>{advance.date || ""}</Typography>
                          </Stack>
                        </Stack>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Card>
            </Grid>
          </Grid>
        </Box>

      </Container>
    </Box>
  );
}

export default Dashboard;